/**
 * Client-side geo helpers — mirrors `app/services/location.py`.
 *
 * Feb 2026 — Dispatch map / order card display uses this to compute
 * driver-to-customer distance + a rough ETA without a server round-trip.
 * The backend still stamps an authoritative `eta_at` via Mapbox
 * driving-traffic; when it's fresh we prefer that number. When it's
 * absent (initial ping) or stale, the client haversine gives dispatch
 * an instant fallback that updates on every SSE `order.location_updated`
 * event.
 *
 * Kept dependency-free so any component can import it.
 */

const EARTH_RADIUS_M = 6_371_000;
const METERS_PER_MILE = 1609.344;

// Average urban driving speed used when the client has to fake an ETA.
// 25 mph ≈ 11.176 m/s. Chosen to slightly UNDER-estimate free-flow so
// dispatch isn't tempted to over-promise the customer.
const AVG_DRIVING_MPS = 25 * (METERS_PER_MILE / 3600);

/**
 * Great-circle distance in metres. Order of magnitude matches
 * `_haversine_m` in `app/services/location.py`.
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}
 */
export function haversineM(lat1, lng1, lat2, lng2) {
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function metersToMiles(m) {
  return m / METERS_PER_MILE;
}

/**
 * Rough ETA in minutes from a great-circle distance. Applies a 1.35x
 * detour factor (city grids rarely deliver a straight-line route).
 * @param {number} meters
 * @returns {number}
 */
export function heuristicEtaMinutes(meters) {
  if (!Number.isFinite(meters) || meters <= 0) return 0;
  const detourAdjusted = meters * 1.35;
  return Math.max(1, Math.round(detourAdjusted / AVG_DRIVING_MPS / 60));
}

/**
 * Prefer the server-stamped `eta_at` when it's fresh (< 2 min old);
 * otherwise fall back to a client-side heuristic. Returns a minutes
 * integer or null when we have nothing to display.
 * @param {{driver_location?: {lat:number, lng:number}, delivery_location?: {lat:number, lng:number}, eta_at?: string, eta_computed_at?: string}} order
 * @returns {{minutes: number|null, miles: number|null, source: 'server'|'client'|'none'}}
 */
export function driverToCustomerEta(order) {
  const drv = order?.driver_location;
  const dst = order?.delivery_location;
  if (!drv || !dst || drv.lat == null || dst.lat == null) {
    return { minutes: null, miles: null, source: 'none' };
  }
  const distMeters = haversineM(drv.lat, drv.lng, dst.lat, dst.lng);
  const miles = metersToMiles(distMeters);

  // Prefer server ETA when fresh (staleness matches backend TTL).
  if (order.eta_at) {
    const eta = new Date(order.eta_at).getTime();
    const now = Date.now();
    const minutesLeft = Math.round((eta - now) / 60_000);
    // Only trust it when eta_computed_at is within 2 minutes.
    let fresh = false;
    if (order.eta_computed_at) {
      const stamped = new Date(order.eta_computed_at).getTime();
      fresh = now - stamped < 2 * 60_000;
    }
    if (fresh && minutesLeft >= 0 && minutesLeft < 240) {
      return { minutes: minutesLeft, miles, source: 'server' };
    }
  }

  return { minutes: heuristicEtaMinutes(distMeters), miles, source: 'client' };
}
